import React, { useState } from "react";
import { useNode } from "@craftjs/core";
import "../../styles/components/user/UserComponents.css";
import "../../styles/landing.css";

interface ImageProps {
  src?: string;
  alt?: string;
  width?: string;
  height?: string;
  margin?: string;
  padding?: string;
  borderRadius?: number;
}

export const Image = ({
  src = "https://via.placeholder.com/400x300",
  alt = "Placeholder Image",
  width = "100%",
  height = "auto",
  margin = "0",
  padding = "0",
  borderRadius = 0,
}: ImageProps) => {
  const {
    connectors: { connect, drag },
    selected,
  } = useNode((node) => ({
    selected: node.events.selected,
  }));

  // Add state to handle image load errors
  const [hasError, setHasError] = useState(false);

  // Create fallback style for when image fails to load
  const fallbackStyle = {
    width: width || "100%",
    height: height || "200px",
    backgroundColor: "#f0f0f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: `${borderRadius}px`,
    color: "#666",
    fontSize: "14px",
    border: "1px dashed #ccc",
  };

  return (
    <div
      ref={(ref: HTMLDivElement | null) => {
        if (ref) connect(drag(ref));
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
      className="image-component"
      style={{
        outline: selected ? "2px solid blue" : "none",
        margin,
        padding,
        position: "relative",
      }}
    >
      <div className="image-container">
        {!hasError ? (
          <img
            src={src}
            alt={alt}
            style={{
              width,
              height: height || "auto",
              borderRadius: `${borderRadius}px`,
            }}
            onClick={(e) => e.stopPropagation()}
            onError={() => setHasError(true)}
          />
        ) : (
          <div style={fallbackStyle}>{alt || "Image not available"}</div>
        )}
      </div>
    </div>
  );
};

export const ImageSettings = () => {
  const {
    actions: { setProp },
    props,
  } = useNode((node) => ({
    props: node.data.props,
  }));

  return (
    <div className="settings-container">
      <h4>Image Settings</h4>
      <div className="form-group">
        <label>Image URL</label>
        <input
          type="text"
          className="form-control"
          value={props.src || ""}
          onChange={(e) =>
            setProp((props: ImageProps) => (props.src = e.target.value))
          }
          placeholder="https://example.com/image.jpg"
        />
      </div>
      <div className="form-group">
        <label>Alt Text</label>
        <input
          type="text"
          className="form-control"
          value={props.alt || ""}
          onChange={(e) =>
            setProp((props: ImageProps) => (props.alt = e.target.value))
          }
        />
      </div>
      <div className="form-group">
        <label>Width</label>
        <input
          type="text"
          className="form-control"
          value={props.width || ""}
          onChange={(e) =>
            setProp((props: ImageProps) => (props.width = e.target.value))
          }
          placeholder="e.g. 100%, 300px"
        />
      </div>
      <div className="form-group">
        <label>Height</label>
        <input
          type="text"
          className="form-control"
          value={props.height || ""}
          onChange={(e) =>
            setProp((props: ImageProps) => (props.height = e.target.value))
          }
          placeholder="e.g. 300px, auto"
        />
      </div>
      <div className="form-group">
        <label>Border Radius (px)</label>
        <input
          type="number"
          className="form-control"
          value={props.borderRadius || 0}
          onChange={(e) =>
            setProp(
              (props: ImageProps) =>
                (props.borderRadius = parseInt(e.target.value))
            )
          }
        />
      </div>
      <div className="form-group">
        <label>Margin</label>
        <input
          type="text"
          className="form-control"
          value={props.margin || ""}
          onChange={(e) =>
            setProp((props: ImageProps) => (props.margin = e.target.value))
          }
          placeholder="e.g. 10px 0"
        />
      </div>
      <div className="form-group">
        <label>Padding</label>
        <input
          type="text"
          className="form-control"
          value={props.padding || ""}
          onChange={(e) =>
            setProp((props: ImageProps) => (props.padding = e.target.value))
          }
          placeholder="e.g. 10px"
        />
      </div>
    </div>
  );
};

Image.craft = {
  props: {
    src: "https://via.placeholder.com/400x300",
    alt: "Placeholder Image",
    width: "100%",
    height: "auto",
    margin: "0",
    padding: "0",
    borderRadius: 0,
  },
  related: {
    settings: ImageSettings,
  },
};
